import { useMemo } from 'react';

import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import RowIfNotEmpty from './Row';
import DialogWrapper from './Wrapper';
import { IntegrationMetadata } from '../../../lib/interface';
import { getLocalTimezoneCode, utcDateTimeToLocal } from '../../../utils/format';
import { formatIntegrationMetadataForDisplay } from '../utils/formatIntegrationMetadata';

type MandateDetailsProps = {
  title: string;
  open: boolean;
  handleClose: () => void;
  details: MandateDetailsData;
};

type MandateDetailsData = {
  payerName: string;
  payerType: string;
  institutionName?: string;
  institutionId?: string;
  accountNumber?: string;
  currency: string;
  description: string;
  startDate: string;
  endDate: string;
  maxTxnAmount: string;
  maxTxnAmountSetByPayerInitial: string;
  maxTotalAmount: string;
  maxTxnCount?: number;
  period: string;
  collectionEntityName?: string;
  processorEntityName?: string;
  recipientName: string;
  bankReference: string;
  ddaReference?: string;
  upiId?: string;
  customerAppId: string;
  externalUserId: string;
  finverseUserId: string;
  finverseAuthorizationReference?: string;
  mandateId: string;
  paymentMethodId: string;
  recipientAccountId: string;
  senderAccountId?: string;
  settlementAccountReference?: string;
  businessUnits?: string[];
  createdAt: string;
  updatedAt: string;
  live?: boolean;
  metadata?: Record<string, string>;
  integration_metadata?: IntegrationMetadata;
};

export function MandateDetails({ title, open, handleClose, details }: MandateDetailsProps): JSX.Element {
  const localTimezoneCode = getLocalTimezoneCode();
  return (
    <DialogWrapper id="" onClose={handleClose} aria-labelledby="customized-dialog-title" title={title} open={open}>
      <DialogContent dividers>
        <Table>
          <TableBody>
            {RowIfNotEmpty('Payer Name', details.payerName)}
            {RowIfNotEmpty('Payer Type', details.payerType)}
            {RowIfNotEmpty('Institution Name', details.institutionName)}
            {RowIfNotEmpty('Institution ID', details.institutionId)}
            {RowIfNotEmpty('Account Number', details.accountNumber)}
            {RowIfNotEmpty('Currency', details.currency)}
            {RowIfNotEmpty('Description', details.description)}
            {RowIfNotEmpty('Start Date', details.startDate)}
            {RowIfNotEmpty('End Date', details.endDate)}
            {RowIfNotEmpty('Maximum Total Amount', details.maxTotalAmount)}
            {RowIfNotEmpty('Maximum Transaction Count', details.maxTxnCount?.toString())}
            {RowIfNotEmpty('Maximum Transaction Amount (set by payer)', details.maxTxnAmountSetByPayerInitial)}
            {RowIfNotEmpty('Maximum Transaction Amount (set by merchant)', details.maxTxnAmount)}
            {RowIfNotEmpty('Period', details.period)}
            {RowIfNotEmpty('Collection Entity Name', details.collectionEntityName)}
            {RowIfNotEmpty('Processor Entity Name', details.processorEntityName)}
            {RowIfNotEmpty('Recipient Name', details.recipientName)}
            {RowIfNotEmpty('Bank Reference', details.bankReference)}
            {RowIfNotEmpty('Customer App ID', details.customerAppId)}
            {RowIfNotEmpty('DDA Reference', details.ddaReference)}
            {RowIfNotEmpty('UPI ID', details.upiId)}
            {RowIfNotEmpty('External User ID', details.externalUserId)}
            {RowIfNotEmpty('Finverse User ID', details.finverseUserId)}
            {RowIfNotEmpty('Finverse Authorization Reference', details.finverseAuthorizationReference)}
            {RowIfNotEmpty('Mandate ID', details.mandateId)}
            {RowIfNotEmpty('Payment Method ID', details.paymentMethodId)}
            {RowIfNotEmpty('Recipient Account ID', details.recipientAccountId)}
            {RowIfNotEmpty('Sender Account ID', details.senderAccountId)}
            {RowIfNotEmpty('Settlement Account Reference', details.settlementAccountReference)}
            {RowIfNotEmpty('Business Units', details.businessUnits?.join(', '), { truncate: true })}
            {RowIfNotEmpty(
              `Created At (${localTimezoneCode})`,
              details.createdAt ? utcDateTimeToLocal(details.createdAt) : undefined,
            )}
            {RowIfNotEmpty(
              `Updated At (${localTimezoneCode})`,
              details.updatedAt ? utcDateTimeToLocal(details.updatedAt) : undefined,
            )}
            {RowIfNotEmpty('Live', details.live === undefined ? undefined : details.live ? 'True' : 'False')}
          </TableBody>
        </Table>
        <MetadataSection metadata={details.metadata} />
        <IntegrationMetadataSection integration_metadata={details.integration_metadata} />
      </DialogContent>
    </DialogWrapper>
  );
}

type CardDetailsData = {
  payerName?: string;
  cardholderName?: string;
  cardBrand: string;
  cardLast4: string;
  cardExpiryMonth?: string;
  cardExpiryYear?: string;
  cardCountry?: string;
  collectionEntityName?: string;
  recipientName?: string;
  acquirerAuthorizationReference?: string;
  finverseAuthorizationReference?: string;
  processorId?: string;
  processorReference?: string;
  paymentMethodId: string;
  customerAppId: string;
  externalUserId?: string;
  finverseUserId?: string;
  recipientAccountId?: string;
  settlementAccountReference?: string;
  businessUnits?: string[];
  createdAt?: string;
  updatedAt?: string;
  metadata?: Record<string, string>;
  integration_metadata?: IntegrationMetadata;
};

type CardDetailsProps = {
  title: string;
  open: boolean;
  handleClose: () => void;
  details: CardDetailsData;
};

export function CardDetails({ title, open, handleClose, details }: CardDetailsProps): JSX.Element {
  const cardExpiry =
    details.cardExpiryMonth && details.cardExpiryYear ? `${details.cardExpiryMonth}/${details.cardExpiryYear}` : '';
  const localTimezoneCode = getLocalTimezoneCode();
  return (
    <DialogWrapper
      id="card-details-modal"
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      title={title}
      open={open}
    >
      <DialogContent dividers>
        <Table>
          <TableBody>
            {RowIfNotEmpty('Payer Name', details.payerName)}
            {RowIfNotEmpty('Cardholder Name', details.cardholderName)}
            {RowIfNotEmpty('Brand', details.cardBrand)}
            {RowIfNotEmpty('Last 4', details.cardLast4)}
            {RowIfNotEmpty('Expiry', cardExpiry, { valueClass: 'no-ligature' })}
            {RowIfNotEmpty('Country', details.cardCountry)}
            {RowIfNotEmpty('Collection Entity Name', details.collectionEntityName)}
            {RowIfNotEmpty('Recipient Name', details.recipientName)}
            {RowIfNotEmpty('Acquirer Authorization Reference', details.acquirerAuthorizationReference)}
            {RowIfNotEmpty('Finverse Authorization Reference', details.finverseAuthorizationReference)}
            {RowIfNotEmpty('Processor ID', details.processorId)}
            {RowIfNotEmpty('Processor Reference', details.processorReference)}
            {RowIfNotEmpty('Customer App ID', details.customerAppId)}
            {RowIfNotEmpty('External User ID', details.externalUserId)}
            {RowIfNotEmpty('Finverse User ID', details.finverseUserId)}
            {RowIfNotEmpty('Payment Method ID', details.paymentMethodId)}
            {RowIfNotEmpty('Recipient Account ID', details.recipientAccountId)}
            {RowIfNotEmpty('Settlement Account Reference', details.settlementAccountReference)}
            {RowIfNotEmpty('Business Units', details.businessUnits?.join(', '), { truncate: true })}
            {RowIfNotEmpty(
              `Created At (${localTimezoneCode})`,
              details.createdAt ? utcDateTimeToLocal(details.createdAt) : undefined,
            )}
            {RowIfNotEmpty(
              `Updated At (${localTimezoneCode})`,
              details.updatedAt ? utcDateTimeToLocal(details.updatedAt) : undefined,
            )}
          </TableBody>
        </Table>
        <MetadataSection metadata={details.metadata} />
        <IntegrationMetadataSection integration_metadata={details.integration_metadata} />
      </DialogContent>
    </DialogWrapper>
  );
}

type MetadataSectionProps = {
  metadata?: Record<string, string>;
};

function MetadataSection({ metadata }: MetadataSectionProps): JSX.Element {
  const sortedRows = Object.entries(metadata ?? {}).sort(([a], [b]) => a.localeCompare(b));

  return (
    <>
      <DialogTitle sx={{ textAlign: 'center' }}>Metadata</DialogTitle>
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableBody>
          {sortedRows.length === 0 ? (
            <TableRow>
              <TableCell align="center" sx={{ color: 'text.disabled' }}>
                None
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map(([label, value]) => RowIfNotEmpty(label, value, { key: label }))
          )}
        </TableBody>
      </Table>
    </>
  );
}

type IntegrationMetadataSectionProps = {
  integration_metadata?: IntegrationMetadata;
};

function IntegrationMetadataSection({ integration_metadata }: IntegrationMetadataSectionProps): JSX.Element {
  const rows = useMemo(() => formatIntegrationMetadataForDisplay(integration_metadata), [integration_metadata]);

  const sortedRows = Object.entries(rows).sort(([a], [b]) => a.localeCompare(b));

  if (integration_metadata?.integration_id === undefined && sortedRows.length === 0) return <></>;

  return (
    <>
      <DialogTitle sx={{ textAlign: 'center' }}>Integration Metadata</DialogTitle>
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableBody>
          {RowIfNotEmpty('Integration ID', integration_metadata?.integration_id)}
          {sortedRows.map(([label, value]) => RowIfNotEmpty(label, value, { key: label }))}
        </TableBody>
      </Table>
    </>
  );
}
